import { useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconMessageChatbot,
  IconRefresh,
  IconSend,
  IconX,
} from '@tabler/icons-react';
import JuiceAdapter from '../adapters/juiceAdapter';
import JuiceChart from './JuiceChart';

/**
 * Floating assistant that answers questions about the documents the signed-in
 * user is allowed to see.
 *
 * Every question goes through /juiceAsk on our own api, which derives the
 * caller's scope from their user record and holds the RAG service's proxy
 * secret. Nothing here knows that secret, and nothing here chooses a scope -
 * if this component asked the service directly, any user could claim any scope.
 *
 * Answers are computed by exact MongoDB aggregation, not by the language model
 * reading documents, so the numbers reconcile with the app's own grids.
 *
 * ADAPT BEFORE USE:
 *   - SUGGESTIONS below must name metrics and queues this customer's Pack
 *     actually declares. A suggestion the Pack cannot answer is the first
 *     thing a new user clicks, and it fails in front of them.
 *   - BRAND_PRIMARY is the icon colour; point it at your own design token.
 *   - the import paths assume the host app's layout (adapters/, components/).
 */

// Replace with questions this customer's Pack can answer. Check them against
// `npm run pack:test` fixtures before shipping.
const SUGGESTIONS = [
  'How many records were created last month?',
  'Which suppliers have the highest total value?',
  'How many documents are sitting in each queue?',
];

// The host app's primary colour. Replace with your own token or a literal.
const BRAND_PRIMARY = '#039DC8';

function JuiceAssistant() {
  const [open, setOpen] = useState(false);
  // Charts of 15+ suppliers are legible but cramped at the default width, and
  // the export buttons wrap onto three rows. Expanding trades the view of the
  // page behind for room to actually read a result.
  const [expanded, setExpanded] = useState(false);
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState(null);
  const conversationId = useRef(null);
  const viewport = useRef(null);

  useEffect(() => {
    // Keep the latest answer in view as turns are appended.
    viewport.current?.scrollTo({
      top: viewport.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [turns, pending]);

  const submit = async (asked) => {
    const text = String(asked ?? question).trim();
    if (!text || pending) return;

    setPending(true);
    setError(null);
    setQuestion('');
    setTurns((previous) => [...previous, { role: 'user', text }]);

    try {
      const result = await JuiceAdapter.ask(text, conversationId.current);
      conversationId.current = result.conversationId || conversationId.current;
      if (result.scope) setScope(result.scope);
      setTurns((previous) => [
        ...previous,
        {
          role: 'assistant',
          text: result.answer,
          visualization: result.visualization,
          sources: result.sources || [],
        },
      ]);
    } catch (cause) {
      // The api passes the service's own wording through for a refused
      // question (422), which is more useful than a generic failure: it says
      // what the assistant cannot answer and why.
      const message =
        cause?.response?.data?.message ||
        cause?.message ||
        'The assistant could not answer that';
      setError(message);
      setTurns((previous) => [...previous, { role: 'error', text: message }]);
    } finally {
      setPending(false);
    }
  };

  const reset = () => {
    conversationId.current = null;
    setTurns([]);
    setError(null);
  };

  if (!open) {
    return (
      <Tooltip label="Ask Juice" position="left">
        <ActionIcon
          size={52}
          radius="xl"
          variant="filled"
          color="cyan"
          onClick={() => setOpen(true)}
          aria-label="Open the Juice assistant"
          style={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 300,
            boxShadow: '0 6px 20px rgba(0,0,0,0.22)',
          }}>
          <IconMessageChatbot size={26} />
        </ActionIcon>
      </Tooltip>
    );
  }

  return (
    <Paper
      shadow="xl"
      radius="md"
      withBorder
      style={{
        position: 'fixed',
        right: 24,
        bottom: 24,
        zIndex: 300,
        width: expanded
          ? 'min(880px, calc(100vw - 48px))'
          : 'min(440px, calc(100vw - 48px))',
        maxHeight: expanded
          ? 'calc(100vh - 48px)'
          : 'min(640px, calc(100vh - 48px))',
        transition: 'width 150ms ease, max-height 150ms ease',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
      <Group
        position="apart"
        p="sm"
        style={{ borderBottom: '1px solid #e9ecef' }}>
        <Group spacing={8}>
          <IconMessageChatbot size={20} color={BRAND_PRIMARY} />
          <Text fw={600} size="sm">
            Juice Assistant
          </Text>
        </Group>
        <Group spacing={4}>
          <Tooltip label={expanded ? 'Shrink' : 'Expand for a closer look'}>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setExpanded((previous) => !previous)}
              aria-label={
                expanded ? 'Shrink the assistant' : 'Expand the assistant'
              }>
              {expanded ? (
                <IconArrowsMinimize size={16} />
              ) : (
                <IconArrowsMaximize size={16} />
              )}
            </ActionIcon>
          </Tooltip>
          <Tooltip label="New conversation">
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={reset}
              aria-label="Start a new conversation">
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => setOpen(false)}
            aria-label="Close the assistant">
            <IconX size={16} />
          </ActionIcon>
        </Group>
      </Group>

      <ScrollArea.Autosize mah={430} viewportRef={viewport} p="sm">
        <Stack spacing="sm">
          {turns.length === 0 ? (
            <Stack spacing={8}>
              <Text size="sm" c="dimmed">
                Ask about invoices, queues and suppliers. Answers are calculated
                directly from the database and cover only the documents you are
                permitted to see.
              </Text>
              {SUGGESTIONS.map((suggestion) => (
                <Text
                  key={suggestion}
                  size="xs"
                  c="cyan.8"
                  style={{ cursor: 'pointer' }}
                  onClick={() => submit(suggestion)}>
                  {suggestion}
                </Text>
              ))}
            </Stack>
          ) : null}

          {turns.map((turn, index) => {
            if (turn.role === 'user') {
              return (
                <Box
                  key={index}
                  style={{ alignSelf: 'flex-end', maxWidth: '85%' }}>
                  <Paper bg="cyan.0" p="xs" radius="md">
                    <Text size="sm">{turn.text}</Text>
                  </Paper>
                </Box>
              );
            }
            if (turn.role === 'error') {
              return (
                <Alert
                  key={index}
                  color="red"
                  icon={<IconAlertCircle size={16} />}
                  p="xs">
                  <Text size="sm">{turn.text}</Text>
                </Alert>
              );
            }
            return (
              <Box key={index}>
                <Paper bg="gray.0" p="xs" radius="md">
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {turn.text}
                  </Text>
                  <JuiceChart visualization={turn.visualization} />
                </Paper>
                {turn.sources?.length ? (
                  <Text size="xs" c="dimmed" mt={4}>
                    {turn.sources.length} source document
                    {turn.sources.length === 1 ? '' : 's'}
                  </Text>
                ) : null}
              </Box>
            );
          })}

          {pending ? (
            <Group spacing={8}>
              <Loader size="xs" color="cyan" />
              <Text size="xs" c="dimmed">
                Working it out…
              </Text>
            </Group>
          ) : null}
        </Stack>
      </ScrollArea.Autosize>

      <Box p="sm" style={{ borderTop: '1px solid #e9ecef' }}>
        <Group spacing={8} align="flex-end" noWrap>
          <Textarea
            value={question}
            onChange={(event) => setQuestion(event.currentTarget.value)}
            placeholder="Ask a question…"
            autosize
            minRows={1}
            maxRows={4}
            style={{ flex: 1 }}
            disabled={pending}
            onKeyDown={(event) => {
              // Enter sends; Shift+Enter is a newline.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
          />
          <ActionIcon
            size={36}
            radius="md"
            variant="filled"
            color="cyan"
            onClick={() => submit()}
            disabled={pending || !question.trim()}
            aria-label="Send the question">
            <IconSend size={18} />
          </ActionIcon>
        </Group>
        {scope ? (
          <Tooltip label={scope.description} multiline w={260}>
            <Badge
              size="xs"
              variant="light"
              color={scope.unrestricted ? 'gray' : 'cyan'}
              mt={6}>
              {scope.unrestricted ? 'All branches' : 'Scoped to your branches'}
            </Badge>
          </Tooltip>
        ) : null}
        {error && turns.length === 0 ? (
          <Text size="xs" c="red" mt={6}>
            {error}
          </Text>
        ) : null}
      </Box>
    </Paper>
  );
}

export default JuiceAssistant;
